import { createMutation, invalidateQueries } from '@amzn/gsce-shared-react-lib';
import { type UpdateAuditQuestionResponseCommandInput } from '@amzn/gbsa-helios-typescript-client';
import type { UpdateAuditQuestionResponseRequest } from 'src/types/helios/QuestionResponse';
import { questionResponseService, withRetry } from '../../../shared/data';
import { AuditQueryKeys } from './keys';
import { cancelAuditSummaryQueries } from '../logic/patchAuditSummaryCache';
import {
  rollbackAuditQuestionResponseCache,
  snapshotAndPatchAuditQuestionResponse,
} from '../logic/patchAuditAnswerCaches';
import {
  enrichVariablesWithExpectedToken,
  refreshCachedResponseTokenFromConflict,
} from '../logic/questionResponseTokens';
import {
  AuditResponseSaveScope,
  beginAuditResponseSave,
  buildAuditResponseSaveOperationKey,
  getPreparedAuditResponseSave,
  settleAuditResponseSave,
} from '../logic/auditResponseSaveCoordinator';
import { normalizeVersionConflictError } from '../logic/versionConflict';

/**
 * Mutation hook for updating a single question response (Pass/Fail/NA
 * answer, comment, NA reason, attachment ids). Consumed by the mobile
 * perform-audit page's `useQuestionResponseDraft` — all response
 * writes on the perform page funnel through this mutation.
 *
 * Transport: uses the legacy REST `questionResponseService.update`
 * NOT the typed SDK `apiHeliosClient.send(new UpdateAuditQuestionResponseCommand(...))`.
 * The question-response endpoint returns `respondedAt`/`lastUpdatedAt`/
 * `changeHistory.timestamp` as epoch-millis strings (`"1785426870728"`),
 * which fail Smithy's RFC-3339 deserializer on the 200 response —
 * the request succeeds server-side but the client throws mid-parse.
 * `useAuditQuestionResponseQuery` bypasses the SDK for the same
 * reason; this mutation follows that established pattern.
 *
 * Input type stays SDK-typed (`UpdateAuditQuestionResponseCommandInput`)
 * so callers don't have to know about the legacy shape. The cast at
 * the send site is safe: the legacy REST service just serializes the
 * object to JSON, and both the SDK type and the legacy request type
 * share the same primitive fields on the wire.
 *
 * Optimistic behavior:
 *   - `onMutate`: cancels in-flight audit-summary refetches for the
 *     target auditId, then patches every matching cached summary so
 *     the question's card badge, the completion strip, and (in a
 *     future slice) the navigator all update synchronously. Only
 *     fires the patch when `responseResult` is present on the input
 *     — future comment-only / attachment-only saves will call this
 *     same mutation without a result and shouldn't accidentally
 *     re-write the answer.
 *   - `onError`: rolls back every snapshot from `onMutate`.
 *   - `onSettled` (on error only): invalidates lists/details/summary
 *     so the next refetch reconciles server truth. Success keeps the
 *     optimistic patch — the client is authoritative about the write
 *     it just made, and the summary endpoint appears to read from an
 *     eventually-consistent path that can return pre-write state if
 *     refetched immediately after the mutation resolves. Trusting
 *     the optimistic patch on success avoids the revert-to-old-value
 *     glitch that "invalidate on both" caused.
 *
 * Version-conflict handling:
 *   - `expectedLastUpdatedAt` is enriched from the token sidecar in
 *     `onMutate` (see `enrichVariablesWithExpectedToken`).
 *   - On a 409, the promise chain funnels the raw REST error
 *     through `normalizeVersionConflictError` so consumers observe
 *     a typed `VersionConflictError` (checkable with
 *     `isVersionConflictError`) rather than the wire shape.
 *   - `onError` refreshes the sidecar from the server-returned
 *     `actualVersion` so the auditor's next attempt uses the fresh
 *     token, and `onSettled` invalidates the summary so the UI
 *     reconciles server truth.
 *
 * Branching is deliberately kept in the callee helpers in `logic/`
 * (patch/rollback/normalize/refresh); this file stays a thin wire
 * of hooks and callbacks.
 */
export const useUpdateAuditQuestionResponseMutation = createMutation(
  (input: UpdateAuditQuestionResponseCommandInput) =>
    // Cast: legacy REST request type has stricter required fields
    // (response, subResponses) than the SDK Command input. At the
    // wire level the missing fields are simply omitted from the
    // JSON body, which the server accepts. Cast through `unknown`
    // because the required→optional gap makes a direct cast unsafe
    // to TypeScript even though it's safe at runtime.
    withRetry(() =>
      questionResponseService.update(
        input as unknown as UpdateAuditQuestionResponseRequest,
      ),
    ).catch(normalizeVersionConflictError),
  {
    onMutate: async (queryClient, variables) => {
      // Register before awaiting query cancellation or writing the optimistic
      // answer so the close gate cannot observe "answered" without "saving".
      const saveOperation =
        getPreparedAuditResponseSave(variables) ??
        beginAuditResponseSave(
          variables.auditId,
          variables.questionId,
          buildAuditResponseSaveOperationKey(
            variables.questionId,
            AuditResponseSaveScope.Response,
          ),
        );
      try {
        await cancelAuditSummaryQueries(queryClient, variables.auditId);
        // Sets `expectedLastUpdatedAt` when the sidecar has a token
        // for this question; no-op on fresh load pre-first-poll —
        // server accepts under the optional-for-backward-compat rule.
        enrichVariablesWithExpectedToken(queryClient, variables);
        if (!variables.responseResult) {
          return { snapshots: [], saveOperation };
        }
        const snapshots = snapshotAndPatchAuditQuestionResponse(
          queryClient,
          variables.auditId,
          variables.questionId,
          {
            responseResult: variables.responseResult,
            respondedBy: variables.respondedBy,
          },
        );
        return { snapshots, saveOperation };
      } catch (error: unknown) {
        // React Query has no mutation context when onMutate itself rejects, so
        // settle here rather than leaking a permanently-pending coordinator entry.
        settleAuditResponseSave(saveOperation, error);
        throw error;
      }
    },
    onError: (queryClient, error, variables, context) => {
      if (context?.snapshots?.length) {
        rollbackAuditQuestionResponseCache(queryClient, context.snapshots);
      }
      // On 409, seed the sidecar with the server's `actualVersion`
      // so the auditor's next attempt against this row uses the
      // fresh token instead of waiting for the next poll tick.
      refreshCachedResponseTokenFromConflict(
        queryClient,
        variables.auditId,
        variables.questionId,
        error,
      );
    },
    onSettled: (queryClient, _data, error, _variables, context) => {
      settleAuditResponseSave(context?.saveOperation, error);
      // Invalidate ONLY on error. Success keeps the optimistic
      // patch — see docblock for the rationale. Invalidating on
      // success triggers an immediate summary refetch that can
      // race the write's read-side propagation and clobber the
      // patch with pre-write data, presenting to the user as
      // "the button reverts to the old status after tapping".
      if (error) {
        void invalidateQueries(queryClient, [
          AuditQueryKeys.lists,
          AuditQueryKeys.details,
          AuditQueryKeys.summary,
        ]);
      }
    },
  },
);
